import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Download } from "lucide-react"
import AppLayout from "@/components/AppLayout"
import ChannelMenu from "@/components/ChannelMenu"
import { Button } from "@/components/ui/Button"
import { Card } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import { formatMoneyShort } from "@/lib/money"
import PlanHeader from "@/pages/Plan/components/PlanHeader"
import { AttainmentLine } from "@/pages/Plan/BottomUp/components/ActualsCell"
import { ToolbarGroup } from "@/pages/Revenue/components/RevenueToolbar"
import ScopeBreadcrumb from "@/pages/Revenue/components/ScopeBreadcrumb"
import YearTargetBar from "./components/YearTargetBar"
import { MARKET_ID, SCOPE_TREE } from "./data"
import { type Row, gmPct, planNodeRows, productRows, totalRow } from "./rollup"

type RowKind = "products" | "nodes"

interface Props {
  scope: string | null
  rows: RowKind | null
}

const CURRENCY = "VND"
const PATH = "/wireframes/plan/actuals"
const CELL = "whitespace-nowrap px-4 py-2.5 text-right tabular-nums"
const HEAD = "h-11 whitespace-nowrap px-4 align-middle text-[13px] font-medium text-muted-foreground"
const TOTAL_BG = { background: "color-mix(in srgb, var(--muted) 50%, var(--card))" }

function hrefFor(scope: string, rows: RowKind) {
  return `${PATH}?scope=${scope}&rows=${rows}`
}

function TableRow({ row, kind, isTotal = false }: { row: Row; kind: RowKind; isTotal?: boolean }) {
  const gm = gmPct(row)
  return (
    <tr className="border-b" style={isTotal ? TOTAL_BG : undefined}>
      <td className={`px-4 py-2.5 text-left ${isTotal ? "font-semibold" : ""}`}>
        <div>{row.name}</div>
        {row.subLabel && <div className="text-xs text-muted-foreground">{row.subLabel}</div>}
      </td>
      <td className={`${CELL} ${isTotal ? "font-semibold" : ""}`}>{formatMoneyShort(row.actual, CURRENCY)}</td>
      <td className={CELL}>{formatMoneyShort(row.plan, CURRENCY)}</td>
      <td className="w-40 px-4 py-2.5 text-xs">
        <AttainmentLine actual={row.actual} plan={row.plan} />
      </td>
      <td className={CELL}>{gm == null ? "" : `${gm.toFixed(1)}%`}</td>
      {kind === "products" ? (
        <td className={CELL}>{row.units == null ? "" : row.units.toLocaleString("en-US")}</td>
      ) : (
        <td className={`${CELL} text-xs`}>
          {row.target != null && <YearTargetBar actual={row.actual} target={row.target} currency={CURRENCY} />}
        </td>
      )}
    </tr>
  )
}

// Plan vs Actual, overall: one line per product or per plan node for the
// chosen scope. Actual covers Jan 1 to today; plan is the full plan year.
export default function PlanActualsWireframe({ scope, rows }: Props) {
  const scopeId = SCOPE_TREE.some((n) => n.id === scope) ? (scope as string) : MARKET_ID
  const kind: RowKind = rows === "nodes" ? "nodes" : "products"
  const scopeNode = SCOPE_TREE.find((n) => n.id === scopeId)!
  const [query, setQuery] = useState("")

  const all = useMemo(() => (kind === "products" ? productRows(scopeId) : planNodeRows(scopeId)), [kind, scopeId])
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return all
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.subLabel?.toLowerCase().includes(q))
      .sort((a, b) => b.actual - a.actual)
  }, [all, query])
  const total = totalRow(all, kind === "nodes")

  return (
    <AppLayout
      header={
        <PlanHeader
          name="VN AOP 2026"
          orgNodeName="Vietnam"
          revenueTarget={878_976_883_184}
          currency={CURRENCY}
          beginDate="2026-01-01"
          endDate="2026-12-31"
        />
      }
    >
      <div className="space-y-4">
        <Card className="flex flex-wrap items-end gap-6 px-4 py-3">
          <ToolbarGroup label="Scope">
            <div className="flex h-9 items-center">
              <ChannelMenu
                scopeTree={SCOPE_TREE}
                currentNodeId={scopeId}
                currentNodeName={scopeNode.name}
                hrefForChannel={(slug) => hrefFor(slug, kind)}
                requireLeaf={false}
              />
            </div>
          </ToolbarGroup>
          <ToolbarGroup label="Rows">
            <ToggleGroup
              type="single"
              value={kind}
              onValueChange={(v) => v && router.get(hrefFor(scopeId, v as RowKind), {}, { preserveScroll: true })}
            >
              <ToggleGroupItem value="products" size="sm">Products</ToggleGroupItem>
              <ToggleGroupItem value="nodes" size="sm">Plan nodes</ToggleGroupItem>
            </ToggleGroup>
          </ToolbarGroup>
          <div className="ml-auto flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={kind === "products" ? "Search products…" : "Search plan nodes…"}
              className="h-9 w-56"
            />
            <Button variant="outline" size="sm" className="h-9">
              <Download className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
              Export Past Sales CSV
            </Button>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <ScopeBreadcrumb scopeTree={SCOPE_TREE} scopeId={scopeId} marketId={MARKET_ID} hrefForScope={(slug) => hrefFor(slug, kind)} />
          <div className="border-b px-4 py-3 text-sm">
            Actual: <span className="font-medium">Jan 1 – Oct 2, 2026</span>
            <span className="mx-2 text-muted-foreground">·</span>
            Plan: <span className="font-medium">full year, Jan – Dec 2026</span>
          </div>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b">
                <th className={`${HEAD} text-left`}>{kind === "products" ? "Product" : "Plan node"}</th>
                <th className={`${HEAD} text-right`}>Actual revenue</th>
                <th className={`${HEAD} text-right`}>Plan revenue</th>
                <th className={`${HEAD} text-left`}>% of plan</th>
                <th className={`${HEAD} text-right`}>GM% (actual)</th>
                <th className={`${HEAD} ${kind === "products" ? "text-right" : "text-left"}`}>
                  {kind === "products" ? "Units sold" : "Year target"}
                </th>
              </tr>
            </thead>
            <tbody>
              <TableRow row={total} kind={kind} isTotal />
              {visible.map((r) => <TableRow key={r.id} row={r} kind={kind} />)}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm">Nothing matches “{query}”.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </AppLayout>
  )
}
